import { Autocomplete, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';
import InputLabel from '@components/InputLabel/index';
import RoundedButton from '@components/RoundedButton/index';

import { useUpdateGroup } from '@hooks/api/group';
import { useGetUserGroups } from '@hooks/api/group';

import type { GroupModel } from 'generated/types';

const messages = defineMessages({
  title: {
    id: 'importJobGroup.title',
    defaultMessage: 'Group Name',
  },
  save: {
    id: 'GLOBAL.save',
    defaultMessage: 'Save',
  },
});

const GroupName: React.FC<{
  value: GroupModel;
  /**
   * Callback that fires when a user types in a group name
   */
  onTypeChange?: (value: string) => void;
  /**
   * Callback that fires when a user selects from the group list
   */
  onSelectionChange?: (value: string) => void;
  onSubmit?: () => void;
  /**
   * Is this compone tused in the ImportJobForm?
   */
  formView?: boolean;
}> = ({
  value,
  onSelectionChange,
  onSubmit,
  onTypeChange,
  formView = false,
}) => {
  const { formatMessage } = useIntl();

  const { data: groupData, loading: groupDataLoading } = useGetUserGroups();
  const groupOptions = (groupData ?? []).map(({ group_name }) => group_name);
  const [updateGroup, updateGroupLoading] = useUpdateGroup();
  const loading = groupDataLoading || updateGroupLoading;

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [group, setGroup] = useState<{ name: string; id: string }>({
    name: value.group_name,
    id: value.group_id,
  });

  const handleSelectionChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: string
  ) => {
    onSelectionChange?.(value);
  };
  const handleTypeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setIsEditing(true);
    const { value } = event.target;
    onTypeChange?.(value);
    if (!formView) {
      setGroup((prev) => ({
        ...prev,
        name: value,
      }));
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    setGroup({ name: value.group_name, id: value.group_id });
  };
  const handleCancelEditing = () => {
    setIsEditing(false);
    setGroup({ id: '', name: '' });
  };
  const handleFinishEditing = async () => {
    await updateGroup({
      input: {
        group_name: group.name,
        group_id: group.id,
      },
    });
    onSubmit?.();
    setIsEditing(false);
  };

  const formComponent = (
    <>
      <InputLabel
        label={formatMessage(messages.title)}
        required
        style={{ color: 'primary.main' }}
      />

      <ContentLoader lineCount={1} loading={loading}>
        <Autocomplete
          disableClearable
          freeSolo
          onChange={formView ? handleSelectionChange : undefined}
          options={formView ? groupOptions : []}
          renderInput={(params) => (
            <TextField {...params} margin="dense" onChange={handleTypeChange} />
          )}
          sx={{ width: '100%' }}
          value={value.group_name}
        />
      </ContentLoader>
    </>
  );
  if (formView) {
    return formComponent;
  }

  return (
    <>
      {isEditing && value.group_id === group.id ? (
        <>
          {formComponent}
          <Icon
            clickable
            color="text.secondary"
            name="xmark"
            onClick={handleCancelEditing}
            size="lg"
            type="fas"
          />
          <RoundedButton
            disabled={updateGroupLoading}
            fontSize={14}
            height="30px"
            onClick={handleFinishEditing}
            width="80px"
          >
            {formatMessage(messages.save)}
          </RoundedButton>
        </>
      ) : (
        <>
          <Typography>{value.group_name}</Typography>
          <Icon
            clickable
            color="text.secondary"
            name="pencil"
            onClick={handleStartEditing}
            size="lg"
            type="fas"
          />
        </>
      )}
    </>
  );
};

export default GroupName;
